import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { updateEmployee, getEmployees } from '@/lib/employees';
import { employeesKeys } from '@/hooks/use-employees';
import { useDebouncedValue } from '@/hooks/use-debounce';
import type { EmployeeDetail, EmployeeListItem } from '@/types/employee';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Loader2, Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Manager selection dialog with autocomplete and active filter - supports multiple managers

interface EditManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: EmployeeDetail;
}

function getInitials(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

export function EditManagerDialog({ open, onOpenChange, employee }: EditManagerDialogProps) {
  const formatName = useEmployeeNameFormatter();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedManagerIds, setSelectedManagerIds] = useState<Set<string>>(new Set());
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [filterActiveOnly, setFilterActiveOnly] = useState(true);

  // Debounce search query
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  // Initialize selected managers from employee data
  useEffect(() => {
    if (open) {
      const initialIds = new Set<string>();
      // Add managers from managerRelations (new format)
      if (employee.managers && employee.managers.length > 0) {
        employee.managers.forEach((m) => {
          if (m.id) initialIds.add(m.id);
        });
      }
      // Fallback to legacy managerId for backward compatibility
      else if (employee.managerId) {
        initialIds.add(employee.managerId);
      }
      setSelectedManagerIds(initialIds);
      setSearchQuery('');
      setFilterActiveOnly(true);
      setComboboxOpen(false);
    }
  }, [open, employee.managers, employee.managerId]);

  // Fetch employees for manager selection
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', 'search', debouncedSearchQuery, filterActiveOnly],
    queryFn: () =>
      getEmployees({
        search: debouncedSearchQuery || undefined,
        limit: 50,
        page: 1,
        ...(filterActiveOnly ? { status: 'active' } : {}),
      }),
    enabled: open, // Only fetch when dialog is open
  });

  const managers = useMemo(() => {
    if (!employeesData?.data) return [];
    // Filter out the current employee from the list
    return employeesData.data.filter((emp: EmployeeListItem) => emp.id !== employee.id);
  }, [employeesData, employee.id]);

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateEmployee>[1]) =>
      updateEmployee(employee.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      managerIds: Array.from(selectedManagerIds),
    });
  };

  const toggleManager = (managerId: string) => {
    const newSet = new Set(selectedManagerIds);
    if (newSet.has(managerId)) {
      newSet.delete(managerId);
    } else {
      newSet.add(managerId);
    }
    setSelectedManagerIds(newSet);
  };

  const removeManager = (managerId: string) => {
    const newSet = new Set(selectedManagerIds);
    newSet.delete(managerId);
    setSelectedManagerIds(newSet);
  };

  // Get selected managers data
  const selectedManagers = useMemo(() => {
    return Array.from(selectedManagerIds)
      .map((id) => {
        const found = managers.find((m: EmployeeListItem) => m.id === id);
        if (found) return found;
        // If not found in current list, try to find in employee's managers
        if (employee.managers) {
          const empManager = employee.managers.find((m) => m.id === id);
          if (empManager) {
            return {
              id: empManager.id,
              firstName: empManager.firstName,
              lastName: empManager.lastName,
              avatarUrl: empManager.avatarUrl,
              position: empManager.position,
            } as EmployeeListItem;
          }
        }
        // Fallback to employee.manager for backward compatibility
        if (employee.manager && employee.manager.id === id) {
          return {
            id: employee.manager.id,
            firstName: employee.manager.firstName,
            lastName: employee.manager.lastName,
            avatarUrl: employee.manager.avatarUrl,
            position: employee.manager.position,
          } as EmployeeListItem;
        }
        return null;
      })
      .filter((m): m is EmployeeListItem => m !== null);
  }, [selectedManagerIds, managers, employee.managers, employee.manager]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Managers</DialogTitle>
          <DialogDescription>
            Select one or more managers for this employee. An employee can have multiple managers for different projects.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Selected managers display */}
            {selectedManagers.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Managers</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedManagers.map((manager) => (
                    <Badge key={manager.id} variant="secondary" className="gap-1 pr-1">
                      <Avatar className="h-4 w-4">
                        {manager.avatarUrl && <AvatarImage src={manager.avatarUrl} />}
                        <AvatarFallback className="text-[8px]">
                          {getInitials(manager.firstName, manager.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs">
                        {formatName({ firstName: manager.firstName, lastName: manager.lastName })}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeManager(manager.id)}
                        className="ml-1 rounded-full hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Manager selection combobox */}
            <div className="space-y-2">
              <Label htmlFor="manager">Add Manager</Label>
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="w-full justify-between"
                    disabled={updateMutation.isPending}
                  >
                    <span className="text-muted-foreground">Select manager...</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                      className="!focus-visible:outline-none !focus-visible:ring-0 !focus-visible:ring-offset-0"
                    />
                    <CommandList>
                      {employeesLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      ) : (
                        <>
                          {managers.length === 0 ? (
                            <CommandEmpty>
                              {debouncedSearchQuery ? 'No managers found.' : 'Start typing to search...'}
                            </CommandEmpty>
                          ) : (
                            <CommandGroup>
                              {managers.map((manager: EmployeeListItem) => {
                                const isSelected = selectedManagerIds.has(manager.id);
                                return (
                                  <CommandItem
                                    key={manager.id}
                                    value={manager.id}
                                    onSelect={() => {
                                      toggleManager(manager.id);
                                      setSearchQuery('');
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        isSelected ? 'opacity-100' : 'opacity-0',
                                      )}
                                    />
                                    <div className="flex items-center gap-2">
                                      <Avatar className="h-6 w-6">
                                        {manager.avatarUrl && (
                                          <AvatarImage src={manager.avatarUrl} />
                                        )}
                                        <AvatarFallback className="text-xs">
                                          {getInitials(manager.firstName, manager.lastName)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="flex flex-col">
                                        <span>
                                          {formatName({ firstName: manager.firstName, lastName: manager.lastName })}
                                        </span>
                                        {manager.position?.title && (
                                          <span className="text-xs text-muted-foreground">
                                            {manager.position.title}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          )}
                        </>
                      )}
                    </CommandList>
                  </Command>
                  <div className="border-t p-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="filter-active"
                        checked={filterActiveOnly}
                        onCheckedChange={(checked) => setFilterActiveOnly(checked === true)}
                      />
                      <Label
                        htmlFor="filter-active"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Active employees only
                      </Label>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
